import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { StatusCodes } from 'http-status-codes';
import { IconLoader2 } from '@tabler/icons-react';
import { getErrorStatus } from '@common/utils';
import { WarningMessage } from '@common/components';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '../ui/button';
import { LangContext } from '@/context/LangProvider';
import { useMutateRepublishProject } from '@/reactQuery/post';
import { showToastErrorMessage } from '@/utils/showToastErrorMessage';

interface RepublishProjectModalProps {
  projectId: string;
  projectTitle: string;
  organizationId?: string;
  workspaceId?: string;
  isOpen: boolean;
  onClose: () => void;
}

function RepublishProjectModal({
  projectId,
  projectTitle,
  organizationId,
  workspaceId,
  isOpen,
  onClose,
}: RepublishProjectModalProps) {
  const { lang } = useContext(LangContext);
  const [showRespondentLimitWarning, setShowRespondentLimitWarning] = useState(false);

  const { mutateAsync: mutateAsyncRepublish, isPending } = useMutateRepublishProject(
    organizationId,
    workspaceId,
    projectId,
  );

  const handleRepublish = async () => {
    setShowRespondentLimitWarning(false);

    try {
      await mutateAsyncRepublish();
      toast.success(lang.get('msg.projectSuccessfullyRepublished'));
      onClose();
    } catch (error) {
      // Check if error is 409 (respondent limit reached)
      if (getErrorStatus(error) === StatusCodes.CONFLICT) {
        setShowRespondentLimitWarning(true);
      } else {
        showToastErrorMessage(error);
      }
    }
  };

  const handleClose = () => {
    setShowRespondentLimitWarning(false);
    onClose();
  };

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-1">
            {lang.get('msg.republishProject')}
          </AlertDialogTitle>
          <p className="text-sm text-dark-text">
            {lang.get('msg.republishProjectConfirmation')}
          </p>
          <p className="text-sm text-dark-text mt-2">
            <span className="font-bold">{projectTitle}</span>
          </p>
        </AlertDialogHeader>

        {/* Warning message for respondent limit reached */}
        {showRespondentLimitWarning && (
          <WarningMessage>
            <div>
              <p>{lang.get('msg.respondentLimitReached')}</p>
              <p className="mt-2">
                {lang.get('msg.pleaseIncreaseMaxRespondentsToRepublish')}
              </p>
            </div>
          </WarningMessage>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleClose}>
            {lang.get('msg.cancel')}
          </AlertDialogCancel>
          <Button
            type="button"
            className="bg-primary hover:bg-primary/90"
            onClick={handleRepublish}
            disabled={isPending}
          >
            {isPending ? (
              <span className="flex items-center gap-1.5">
                <IconLoader2 className="animate-spin" size={18} />
                {lang.get('msg.republishing')}...
              </span>
            ) : (
              lang.get('msg.republish')
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default RepublishProjectModal;
